import React, { useState, useEffect, useCallback } from 'react';
import './App.css';
import liff from '@line/liff';
import { supabase } from './supabaseClient';
import CarParkForm from './components/CarParkForm';
import BotMessageManager from './components/admin/BotMessageManager';
import MessageLogDashboard from './components/admin/MessageLogDashboard';
import ExpenseForm from './components/expense/ExpenseForm';
import ExpenseDetail from './components/expense/ExpenseDetail';
import HelpPage from './components/HelpPage';
import AdminZone from './components/admin/AdminZone';
import FeatureManager from './components/admin/FeatureManager';
import RegisterForm from './components/users/RegisterForm';
import FriendSearchModal from './components/users/FriendSearchModal';
import UserManager from './components/admin/UserManager';
import LendingTracker from './components/LendingTracker';
import TripManager from './components/trip/TripManager';
import NotifyPage from './components/notify/NotifyPage';
import { FRIENDSHIP_STATUS, FRIEND_SEARCH_STATUS } from './constants';
import NotInLineGate from './components/NotInLineGate';

const SERVICE_VIEWS = {
  'car-park': 'car-park',
  'expense-tracker': 'expense-form',
  'trip-manager': 'trip-manager',
  'lending_tracker': 'lending-tracker',
  'notify': 'medicine-notify',
};

const SERVICE_ICONS = {
  'expense-tracker': (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
      <line x1="1" y1="10" x2="23" y2="10"></line>
    </svg>
  ),
  'lending_tracker': (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
      <circle cx="9" cy="7" r="4"></circle>
      <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
      <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
    </svg>
  ),
  'trip-manager': (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 2L11 13"></path>
      <path d="M22 2l-7 20-4-9-9-4 20-7z"></path>
    </svg>
  ),
  'car-park': (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4 2.9A3.7 3.7 0 0 0 2 12v4c0 .6.4 1 1 1h2"></path><circle cx="7" cy="17" r="2"></circle><circle cx="17" cy="17" r="2"></circle></svg>
  ),
  'notify': (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
      <line x1="12" y1="8" x2="12" y2="12"></line>
      <line x1="12" y1="16" x2="12.01" y2="16"></line>
    </svg>
  ),
};

function App() {
  const HomeButton = ({ onClick }) => (
    <button
      onClick={onClick}
      className="icon-button home-button"
      aria-label="กลับหน้าหลัก"
      title="กลับหน้าหลัก"
    >
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 11l9-7 9 7"></path>
        <path d="M5 10v10a1 1 0 0 0 1 1h4v-6h4v6h4a1 1 0 0 0 1-1V10"></path>
      </svg>
    </button>
  );

  const [currentView, setCurrentView] = useState('home');
  const [liffProfile, setLiffProfile] = useState(null);
  const [appUser, setAppUser] = useState(null); // เก็บข้อมูล User จาก DB ของเราเอง
  const [isLiffInit, setIsLiffInit] = useState(false);
  const [notInClient, setNotInClient] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [features, setFeatures] = useState([]);
  const [isFriendSearchOpen, setIsFriendSearchOpen] = useState(false);
  const [toast, setToast] = useState(null);
  const [friendRequests, setFriendRequests] = useState([]); // เก็บรายการคำขอเป็นเพื่อน
  const [isBlockModalOpen, setIsBlockModalOpen] = useState(false);
  const [userToBlock, setUserToBlock] = useState(null);
  const [linkInProgress, setLinkInProgress] = useState(false);

  const fetchFriendRequests = useCallback(async (userId) => {
    try {
      // ดึงรายการที่คนอื่น (user_id_1) ขอเรา (user_id_2) เป็นเพื่อน และสถานะเป็น pending
      const { data, error } = await supabase
        .from('friendships')
        .select(`
          id,
          user:users!user_id_1(id, display_name, picture_url, username)
        `)
        .eq('user_id_2', userId)
        .eq('status', FRIENDSHIP_STATUS.PENDING);
      
      if (error) throw error;
      setFriendRequests(data || []);
    } catch (error) {
      console.error('Error fetching friend requests:', error);
    }
  }, []);

  const syncUserWithDatabase = useCallback(async (profile) => {
    try {
      // 1. เช็คว่ามี User นี้หรือยัง
      const { data: existingUser, error: fetchError } = await supabase
        .from('users')
        .select('*')
        .eq('line_user_id', profile.userId)
        .maybeSingle();

      if (fetchError) throw fetchError;

      if (existingUser) {
        setAppUser(existingUser);
        fetchFriendRequests(existingUser.id); // โหลดคำขอเป็นเพื่อน
      } else {
        // 2. ถ้าไม่มี ให้ไปหน้าลงทะเบียน
        setCurrentView('register');
      }
    } catch (error) {
      console.error('Error syncing user:', error);
    }
  }, [fetchFriendRequests]);

  // อัปเดต dependency ของ syncUserWithDatabase หลังจากประกาศ fetchFriendRequests แล้ว (ในทางปฏิบัติ React จะจัดการให้ แต่เพื่อความถูกต้องตามลำดับ)
  // เนื่องจาก useCallback สร้างตัวแปร const เราต้องเรียงลำดับให้ fetchFriendRequests อยู่ก่อน syncUserWithDatabase
  // แต่ใน Javascript hoisting แบบ const ทำไม่ได้ ดังนั้นผมจะสลับลำดับในโค้ดจริงด้านล่างนี้ครับ

  useEffect(() => {
    const initLiff = async () => {
      try {
        if (process.env.REACT_APP_LIFF_ID) {
          await liff.init({ liffId: process.env.REACT_APP_LIFF_ID });
          if (!liff.isInClient()) {
            setNotInClient(true);
            return;
          }
          if (liff.isLoggedIn()) {
            const profile = await liff.getProfile();
            setLiffProfile(profile);
            await syncUserWithDatabase(profile); // Sync ข้อมูลลง DB
          }
        }
      } catch (error) {
        console.error('LIFF Initialization failed', error);
      } finally {
        setIsLiffInit(true);
      }
    };
    initLiff();
  }, [syncUserWithDatabase]);

  const checkIsAdmin = useCallback(async (userId) => {
    const { data } = await supabase
      .from('admins')
      .select('user_id')
      .eq('user_id', userId)
      .maybeSingle();
    setIsAdmin(!!data);
  }, []);

  useEffect(() => {
    if (liffProfile?.userId) {
      checkIsAdmin(liffProfile.userId);
    }
  }, [liffProfile, checkIsAdmin]);

  // Fetch Feature Flags
  useEffect(() => {
    const fetchFeatures = async () => {
      const { data } = await supabase.from('feature_flags').select('*').order('key');
      if (data) {
        setFeatures(data);
      }
    };
    fetchFeatures();
  }, [currentView]); // Reload flags when view changes (e.g. back from admin)

  const handleLogin = () => {
    liff.login();
  };

  const handleLogout = () => {
    liff.logout();
    window.location.reload();
  };

  const showToastMessage = useCallback((message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => {
      setToast(null);
    }, 3000);
  }, []);

  const linkBotAccountByCode = useCallback(async (code) => {
    if (!appUser || !code) return;
    if (linkInProgress) return;
    setLinkInProgress(true);
    try {
      const { data: codeRow, error: codeError } = await supabase
        .from('line_link_codes')
        .select('id, bot_user_id, used_at, expires_at')
        .eq('code', code)
        .maybeSingle();

      if (codeError) throw codeError;
      if (!codeRow || codeRow.used_at) {
        showToastMessage('โค้ดไม่ถูกต้องหรือถูกใช้แล้ว', 'error');
        return;
      }

      const now = new Date();
      if (codeRow.expires_at && new Date(codeRow.expires_at) < now) {
        showToastMessage('โค้ดหมดอายุแล้ว', 'error');
        return;
      }

      const { data: updatedUser, error: updateUserError } = await supabase
        .from('users')
        .update({ line_user_id_bot: codeRow.bot_user_id })
        .eq('id', appUser.id)
        .select()
        .single();
      if (updateUserError) throw updateUserError;

      await supabase
        .from('line_link_codes')
        .update({ used_at: new Date().toISOString() })
        .eq('id', codeRow.id);

      showToastMessage('เชื่อมบัญชีสำเร็จแล้ว');
      if (updatedUser) {
        setAppUser(updatedUser);
      }
    } catch (error) {
      console.error('Link bot account error:', error);
      showToastMessage('เชื่อมบัญชีไม่สำเร็จ', 'error');
    } finally {
      setLinkInProgress(false);
    }
  }, [appUser, linkInProgress, showToastMessage]);

  useEffect(() => {
    if (!appUser || !liffProfile) return;
    const params = new URLSearchParams(window.location.search);
    const code = params.get('link');
    const requestedView = params.get('view');
    const allowedViews = new Set([...Object.values(SERVICE_VIEWS), 'expense-detail', 'help', 'medicine-notify']);

    if (code) {
      linkBotAccountByCode(code);
      params.delete('link');
    }

    if (requestedView && allowedViews.has(requestedView)) {
      setCurrentView(requestedView);
      params.delete('view');
    }

    if (code || requestedView) {
      const next = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ''}`;
      window.history.replaceState({}, '', next);
    }
  }, [appUser, liffProfile, linkInProgress, linkBotAccountByCode]);

  const searchUsers = async (keyword) => {
    if (!appUser || !keyword || keyword.length < 3) return [];
    try {
      // 0. Get blocked users (both directions)
      const { data: blocked } = await supabase
        .from('blocked_users')
        .select('blocker_id, blocked_id')
        .or(`blocker_id.eq.${appUser.id},blocked_id.eq.${appUser.id}`);
      const blockedIds = blocked ? blocked.map(b => b.blocker_id === appUser.id ? b.blocked_id : b.blocker_id) : [];

      // Get reported users
      const { data: reported } = await supabase
        .from('spam_reports')
        .select('reported_id')
        .eq('reporter_id', appUser.id);
      const reportedIds = reported ? reported.map(r => r.reported_id) : [];
      const excludedIds = [...blockedIds, ...reportedIds];

      // 1. Search users
      const { data, error } = await supabase
        .from('users')
        .select('id, username, display_name, picture_url')
        .ilike('username', `%${keyword}%`)
        .neq('id', appUser.id)
        .limit(20);

      if (error) throw error;
      const filteredData = data ? data.filter(u => !excludedIds.includes(u.id)) : [];
      if (filteredData.length === 0) return [];

      // 2. Get friendship status for these users
      const { data: friendships } = await supabase
        .from('friendships')
        .select('user_id_1, user_id_2, status')
        .or(`user_id_1.eq.${appUser.id},user_id_2.eq.${appUser.id}`);

      const statusMap = {};
      if (friendships) {
          friendships.forEach(f => {
              const otherId = f.user_id_1 === appUser.id ? f.user_id_2 : f.user_id_1;
              let status = f.status;
              if (status === FRIENDSHIP_STATUS.PENDING) {
                  status = f.user_id_1 === appUser.id ? FRIEND_SEARCH_STATUS.REQUEST_SENT : FRIEND_SEARCH_STATUS.REQUEST_RECEIVED;
              }
              statusMap[otherId] = status;
          });
      }

      return filteredData.map(u => ({
          ...u,
          status: statusMap[u.id] || FRIEND_SEARCH_STATUS.NONE
      }));
    } catch (error) {
      console.error('Error searching users:', error);
      return [];
    }
  };

  const handleAddFriend = async (targetUsername) => {
    if (!appUser) return;
    try {
      const { data: targetUser } = await supabase.from('users').select('id, display_name').eq('username', targetUsername).single();
      if (!targetUser) throw new Error('ไม่พบผู้ใช้งาน');

      // Insert with 'pending' status
      const { error } = await supabase.from('friendships').insert({ 
        user_id_1: appUser.id, 
        user_id_2: targetUser.id,
        status: FRIENDSHIP_STATUS.PENDING
      });
      
      if (error) throw error;
      showToastMessage(`ส่งคำขอเป็นเพื่อนถึง ${targetUser.display_name} แล้ว`);
      
      // Try to open ShareTargetPicker to notify friend
      if (liff.isApiAvailable('shareTargetPicker')) {
        liff.shareTargetPicker([
          {
            type: "flex",
            altText: "มีคำขอเป็นเพื่อนใหม่จาก " + liffProfile.displayName,
            contents: {
              type: "bubble",
              body: {
                type: "box",
                layout: "vertical",
                contents: [
                  { type: "text", text: "👋 คำขอเป็นเพื่อน", weight: "bold", size: "xl", color: "var(--color-primary)" },
                  { type: "text", text: `${liffProfile.displayName} ต้องการเพิ่มคุณเป็นเพื่อนใน Memmany`, wrap: true, margin: "md" },
                  {
                    type: "button",
                    style: "primary",
                    action: { type: "uri", label: "เปิดแอปเพื่อตอบรับ", uri: "https://liff.line.me/" + liff.id },
                    margin: "lg",
                    color: "var(--color-primary)"
                  }
                ]
              }
            }
          }
        ]).catch(err => console.log('ShareTargetPicker cancelled', err));
      }

    } catch (error) {
      showToastMessage(error.message, 'error');
      throw error;
    }
  };

  const handleRespondRequest = async (requestId, isAccepted) => {
    if (isAccepted) {
      await supabase.from('friendships').update({ status: FRIENDSHIP_STATUS.ACCEPTED }).eq('id', requestId);
      showToastMessage('ตอบรับคำขอแล้ว');
    } else {
      await supabase.from('friendships').delete().eq('id', requestId);
      showToastMessage('ปฏิเสธคำขอแล้ว');
    }
    fetchFriendRequests(appUser.id);
  };

  const handleBlockClick = (targetUser) => {
      setUserToBlock(targetUser);
      setIsBlockModalOpen(true);
  };

  const confirmBlock = async () => {
    if (!appUser || !userToBlock) return;
    try {
      // 1. Add to blocked_users
      await supabase.from('blocked_users').insert({ blocker_id: appUser.id, blocked_id: userToBlock.id });
      
      // 2. Remove friendship if exists (both directions)
      await supabase.from('friendships').delete().or(`and(user_id_1.eq.${appUser.id},user_id_2.eq.${userToBlock.id}),and(user_id_1.eq.${userToBlock.id},user_id_2.eq.${appUser.id})`);
      
      showToastMessage(`บล็อก ${userToBlock.display_name} เรียบร้อยแล้ว`);
      // Refresh requests/friends if needed
      fetchFriendRequests(appUser.id);
    } catch (error) {
      console.error('Error blocking user:', error);
      showToastMessage('เกิดข้อผิดพลาดในการบล็อก', 'error');
    } finally {
        setIsBlockModalOpen(false);
        setUserToBlock(null);
    }
  };

  const handleReportSpam = async (targetUser, reason) => {
     if (!appUser) return;
     if (!reason) return;

     try {
       await supabase.from('spam_reports').insert({ reporter_id: appUser.id, reported_id: targetUser.id, reason });
       showToastMessage(`ส่งรายงานเรียบร้อยแล้ว ขอบคุณที่ช่วยตรวจสอบครับ`);
     } catch (error) {
       showToastMessage('เกิดข้อผิดพลาดในการรายงาน', 'error');
     }
  };

  if (!isLiffInit && !notInClient) return <div className="center-message">Loading...</div>;

  if (notInClient) return <NotInLineGate />;


  if (!liffProfile) {
    return (
      <div className="auth-shell">
        <div className="auth-card">
          <div className="auth-orb" aria-hidden="true" />
          <span className="brand-pill">Memmany</span>
          <h1 className="auth-title">ปลาทองช่วยจำ</h1>
          <p className="auth-tagline">เรื่องยุ่ง ๆ ให้ปลาทองช่วยจำให้</p>
          <button onClick={handleLogin} className="button-primary">
            Login with LINE
          </button>
        </div>
      </div>
    );
  }

  const renderContent = () => {
    // ป้องกันการเข้าถึงหน้า Admin หากไม่ใช่ Admin
    if (['bot-manager', 'message-log', 'feature-manager'].includes(currentView) && !isAdmin) {
      return (
        <div style={{ padding: '40px', textAlign: 'center', color: '#d32f2f' }}>
          <h2>⛔️ Access Denied</h2>
          <p>คุณไม่มีสิทธิ์เข้าถึงหน้านี้ (You are not authorized)</p>
          <button onClick={() => setCurrentView('home')} style={{ marginTop: '20px', padding: '10px 20px', cursor: 'pointer' }}>
            กลับหน้าหลัก
          </button>
        </div>
      );
    }

    if (currentView === 'home' && appUser && !appUser.line_user_id_bot) {
      return (
        <div className="center-shell">
          <div className="link-card">
            <h2>เชื่อมบัญชี LINE Bot ก่อนใช้งาน</h2>
            <p>
              เราได้ส่งลิงก์เชื่อมบัญชีไปที่แชทกับบอทแล้ว<br />
              กรุณาเปิดแชทและกดลิงก์ จากนั้นกลับมาเข้าใหม่อีกครั้ง
            </p>
            <p className="link-hint">หากยังไม่เห็นลิงก์ ให้พิมพ์คำว่า “เชื่อมบัญชี” ในแชทกับบอท</p>
          </div>
        </div>
      );
    }

    switch (currentView) {
      case 'register':
        return (
          <RegisterForm 
            userProfile={liffProfile} 
            onRegisterSuccess={(user) => {
              setAppUser(user);
              setCurrentView('home');
            }} 
          />
        );
      case 'bot-manager':
        return (
          <div>
            <HomeButton onClick={() => setCurrentView('home')} />
            <BotMessageManager />
          </div>
        );
      case 'car-park':
        return (
          <div>
            <HomeButton onClick={() => setCurrentView('home')} />
            <CarParkForm userProfile={liffProfile} appUser={appUser} />
          </div>
        );
      case 'help':
        return <HelpPage />;
      case 'expense-detail':
        return <ExpenseDetail userProfile={liffProfile} appUser={appUser} />;
      case 'expense-form':
        return (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <HomeButton onClick={() => setCurrentView('home')} />
              <h2 style={{ margin: 0, color: 'var(--color-primary)', fontWeight: '700', fontSize: '24px', fontFamily: 'var(--font-display)' }}>รายการรายจ่าย</h2>
            </div>
            <ExpenseForm userProfile={liffProfile} appUser={appUser} />
          </div>
        );
      case 'trip-manager':
        return (
          <div>
            <TripManager 
                appUser={appUser} 
                userProfile={liffProfile}
                onBack={() => setCurrentView('home')}
                onShowToast={showToastMessage}
            />
          </div>
        );
      case 'lending-tracker':
        return (
          <LendingTracker
            appUser={appUser}
            onBack={() => setCurrentView('home')}
            onShowToast={showToastMessage}
          />
        );
      case 'medicine-notify':
        return (
          <NotifyPage
            appUser={appUser}
            onBack={() => setCurrentView('home')}
          />
        );
      case 'message-log':
        return (
          <div>
            <HomeButton onClick={() => setCurrentView('home')} />
            <MessageLogDashboard />
          </div>
        );
      case 'feature-manager':
        return (
          <div>
            <HomeButton onClick={() => setCurrentView('home')} />
            <FeatureManager />
          </div>
        );
      case 'user-manager':
        return (
          <div>
            <HomeButton onClick={() => setCurrentView('home')} />
            <UserManager />
          </div>
        );
      default:
        const activeServices = features.filter(f => f.is_enabled && SERVICE_VIEWS[f.key]);
        const inactiveServices = features.filter(f => !f.is_enabled && SERVICE_VIEWS[f.key]);

        return (
          <div className="home-shell">
            <div className="home-main">
              <header className="hero-card">
                <div className="hero-glow" aria-hidden="true" />
                <div className="hero-bubbles" aria-hidden="true" />
                <div className="hero-header">
                  <div className="brand">
                    <span className="brand-pill">Memmany</span>
                    <h1 className="brand-title">ปลาทองช่วยจำ</h1>
                    <p className="brand-subtitle">เรื่องยุ่ง ๆ ให้ปลาทองช่วยจำให้</p>
                  </div>
                  <button 
                    onClick={() => setIsFriendSearchOpen(true)}
                    className="icon-button friend-button"
                    aria-label="ค้นหาเพื่อน"
                  >
                    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="8.5" cy="7" r="4"></circle><line x1="20" y1="8" x2="20" y2="14"></line><line x1="23" y1="11" x2="17" y2="11"></line></svg>
                    {friendRequests.length > 0 && (
                      <span className="friend-badge">{friendRequests.length}</span>
                    )}
                  </button>
                </div>
                <p className="hero-welcome">Welcome, <strong>{liffProfile.displayName}</strong></p>
                <div className="hero-fish" aria-hidden="true">
                  <svg viewBox="0 0 120 80" aria-hidden="true">
                    <defs>
                      <linearGradient id="goldfish" x1="0" x2="1" y1="0" y2="1">
                        <stop offset="0%" stopColor="#ffb66b" />
                        <stop offset="100%" stopColor="#ff7a1a" />
                      </linearGradient>
                    </defs>
                    <ellipse cx="50" cy="40" rx="28" ry="20" fill="url(#goldfish)" />
                    <path d="M74 40 L112 22 L100 40 L112 58 Z" fill="#ff9b4d" />
                    <circle cx="40" cy="36" r="3.5" fill="#fff" />
                    <circle cx="40" cy="36" r="2" fill="#1f2a33" />
                    <path d="M55 45 Q60 52 66 45" stroke="#e05800" strokeWidth="3" strokeLinecap="round" fill="none" />
                    <path d="M48 25 Q55 20 64 24" stroke="#ff9b4d" strokeWidth="6" strokeLinecap="round" fill="none" opacity="0.8" />
                    <path d="M52 58 Q60 64 70 58" stroke="#ff9b4d" strokeWidth="6" strokeLinecap="round" fill="none" opacity="0.8" />
                  </svg>
                </div>
              </header>

              {/* Active Services Section */}
              {activeServices.length > 0 && (
                <section className="section-card">
                  <h2 className="section-title">วันนี้ให้ช่วยจำอะไรดี ?</h2>
                  <div className="service-grid">
                    {activeServices.map(service => (
                      <button 
                        key={service.key} 
                        onClick={() => setCurrentView(SERVICE_VIEWS[service.key])} 
                        className="service-card"
                        type="button"
                      >
                        <span className="service-icon">
                          {SERVICE_ICONS[service.key] || (
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <circle cx="12" cy="12" r="10"></circle>
                              <line x1="12" y1="8" x2="12" y2="16"></line>
                              <line x1="8" y1="12" x2="16" y2="12"></line>
                            </svg>
                          )}
                        </span>
                        <span className="service-label">
                          {service.key === 'expense-tracker' ? 'จ่าย' : service.key === 'lending_tracker' ? 'ยืม' : service.key === 'trip-manager' ? 'ทริป' : service.key === 'notify' ? 'เตือน' : service.label}
                        </span>
                      </button>
                    ))}
                  </div>
                </section>
              )}

              {/* Inactive Services Section */}
              {inactiveServices.length > 0 && (
                <section className="section-card section-card--muted">
                  <h2 className="section-title section-title--muted">บริการที่ปิดปรับปรุง</h2>
                  <div className="service-grid">
                    {inactiveServices.map(service => (
                      <div key={service.key} className="service-card service-card--inactive">
                        <span className="service-icon service-icon--inactive">
                          {SERVICE_ICONS[service.key] || (
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <circle cx="12" cy="12" r="10"></circle>
                              <line x1="12" y1="8" x2="12" y2="16"></line>
                              <line x1="8" y1="12" x2="16" y2="12"></line>
                            </svg>
                          )}
                        </span>
                        <span className="service-label">
                          {service.key === 'expense-tracker' ? 'จ่าย' : service.key === 'lending_tracker' ? 'ยืม' : service.key === 'trip-manager' ? 'ทริป' : service.key === 'notify' ? 'เตือน' : service.label}
                        </span>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* Admin Section */}
              {isAdmin && (
                <section className="section-card admin-card">
                  <AdminZone onNavigate={setCurrentView} />
                </section>
              )}
            </div>

            {/* Logout Section */}
            <div className="logout-section">
              <button onClick={handleLogout} className="button-ghost">
                Logout
              </button>
            </div>

            {isFriendSearchOpen && (
              <FriendSearchModal
                  onClose={() => setIsFriendSearchOpen(false)}
                  onSearchUsers={searchUsers}
                  onAddFriend={handleAddFriend}
                  friendRequests={friendRequests}
                  onRespondRequest={handleRespondRequest}
                  onBlockUser={handleBlockClick}
                  onReportSpam={handleReportSpam}
              />
            )}

            {toast && (
              <div style={{
                  position: 'fixed',
                  bottom: '30px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  backgroundColor: toast.type === 'error' ? '#f44336' : 'var(--color-text)',
                  color: '#fff',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  zIndex: 3000,
                  fontSize: '14px',
                  textAlign: 'center',
                  minWidth: '200px',
                  animation: 'fadeIn 0.3s ease-in-out'
              }}>
                  {toast.message}
              </div>
            )}

            {isBlockModalOpen && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 2200 }}>
                    <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '16px', width: '90%', maxWidth: '400px', boxShadow: '0 20px 40px rgba(0,0,0,0.1)', textAlign: 'center' }}>
                        <h3 style={{ margin: '0 0 15px 0', color: 'var(--color-text)' }}>ยืนยันการบล็อก</h3>
                        <p style={{ color: 'var(--color-text-muted)', marginBottom: '15px' }}>คุณต้องการบล็อก <strong>{userToBlock?.display_name}</strong> ใช่หรือไม่?</p>
                        <p style={{ color: '#d32f2f', fontSize: '13px', marginBottom: '25px', backgroundColor: '#ffebee', padding: '10px', borderRadius: '8px' }}>⚠️ หากเป็นเพื่อนกันอยู่ ระบบจะลบความเป็นเพื่อนออกด้วย</p>
                        <div style={{ display: 'flex', justifyContent: 'center', gap: '15px' }}>
                            <button 
                                onClick={() => setIsBlockModalOpen(false)}
                                style={{ padding: '10px 20px', backgroundColor: '#fff', color: 'var(--color-text-muted)', border: '1px solid var(--color-border)', borderRadius: '8px', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer' }}
                            >
                                ยกเลิก
                            </button>
                            <button 
                                onClick={confirmBlock}
                                style={{ padding: '10px 20px', backgroundColor: '#f44336', color: 'white', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer', boxShadow: '0 4px 6px rgba(244, 67, 54, 0.3)' }}
                            >
                                บล็อกผู้ใช้
                            </button>
                        </div>
                    </div>
                </div>
            )}
          </div>
        );
    }
  };

  return (
    <div className="App">
      {renderContent()}
    </div>
  );
}

export default App;
